/*
  The theme registry.

  One entry per `[data-theme]` block in packages/theme/tokens.css. This file is what the picker
  renders and what the boot script in index.html validates against; the stylesheet is
  what actually paints. They are two halves of one decision, so themes.test.ts
  fails if they drift — a theme listed here with no block would render as Sheet with
  a different name, which is the failure nobody notices until a screenshot.

  Adding a theme is a block in packages/theme/tokens.css plus an entry here. Nothing else.

  The set is One Sheet (docs/adr/0011): the landing page's world carried into the app.
*/

export const THEME_IDS = ["sheet", "cobalt", "ink", "saffron"] as const;

export type ThemeId = (typeof THEME_IDS)[number];

/*
  Two families, and a family is a ground: `paper` is the landing's bone paper with
  warm ink type, `ink` is the landing's footer sheet — warm ink with paper type.
  Within a family the grounds, text, hairlines and corners are identical; a theme is
  an accent. Unlike the modern/minimal split this replaced (ADR 0006), family here
  IS polarity, so it is also what decides `color-scheme`.
*/
export type ThemeFamily = "paper" | "ink";

export const THEME_FAMILIES: readonly ThemeFamily[] = ["paper", "ink"];

export type Theme = {
  id: ThemeId;
  /* Proper nouns. Deliberately not translated — a theme keeps its name in every locale. */
  name: string;
  family: ThemeFamily;
  /* i18n key under y.settings.appearance.themes for the one-line description. */
  descriptionKey: string;
};

export const THEMES: readonly Theme[] = [
  {
    id: "sheet",
    name: "Sheet",
    family: "paper",
    descriptionKey: "y.settings.appearance.themes.sheet",
  },
  {
    id: "cobalt",
    name: "Cobalt",
    family: "paper",
    descriptionKey: "y.settings.appearance.themes.cobalt",
  },
  {
    id: "ink",
    name: "Ink",
    family: "ink",
    descriptionKey: "y.settings.appearance.themes.ink",
  },
  {
    id: "saffron",
    name: "Saffron",
    family: "ink",
    descriptionKey: "y.settings.appearance.themes.saffron",
  },
];

/*
  What a new account sees after signing in, until it picks something else: Ink,
  the landing's footer sheet, which is also the iOS app's dark ground (owner's
  decision, 2026-10-10). Sheet is still the reference theme and the `:root`
  block in tokens.css, so anything outside the picker's reach (apps/admin, a
  page painted before the boot script) stays on paper; only the stored default
  moved. The public pages do not follow the picker at all (DESIGN.md §9).
*/
export const DEFAULT_THEME: ThemeId = "ink";

/*
  Where a theme from the previous set lands (ADR 0011). A saved value is migrated to
  the nearest new theme rather than dropped to the default: someone who chose a dark
  theme keeps a dark app. Cobalt is not here because the id still exists — it moved
  from dark to light, and keeps its name and its hue.

  Duplicated as a literal in index.html's boot script, which runs before any module
  and rewrites the stored value; themes.test.ts fails if the two maps disagree.
*/
export const LEGACY_THEMES: Readonly<Record<string, ThemeId>> = {
  midnight: "ink",
  filament: "ink",
  juniper: "ink",
  graphite: "ink",
  ember: "saffron",
  paper: "sheet",
};

/* The localStorage key. Duplicated as a literal in index.html's boot script — that
   script runs before any module loads, which is the whole point of it. */
export const THEME_STORAGE_KEY = "astring-theme";

export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === "string" && (THEME_IDS as readonly string[]).includes(value);
}

/*
  A stored value, made safe for the DOM: a current id passes, a retired id maps to
  its successor, anything else is null and the caller falls back to the default.
  An own-property check so a stored "constructor" cannot resolve through the
  prototype (`Object.hasOwn` is ES2022; this app targets ES2020).
*/
export function resolveThemeId(value: unknown): ThemeId | null {
  if (isThemeId(value)) return value;
  if (typeof value === "string" && Object.prototype.hasOwnProperty.call(LEGACY_THEMES, value)) {
    return LEGACY_THEMES[value];
  }
  return null;
}

export function themesByFamily(family: ThemeFamily): Theme[] {
  return THEMES.filter((theme) => theme.family === family);
}
